// MARK://dhk8/admin-shell/lib/gql
// Cliente GraphQL (urql) del Admin — apunta al graphql-gateway a través del
// rest-proxy (`${apiOrigin()}/graphql`, MISMO origin que el REST → reusa CORS).
//
// Reusa el auth de lib/api.ts: inyecta `Authorization: Bearer <jwt>` +
// `x-dhk8-tenant-id` en cada request y, ante un 401, rota el token con el
// MISMO single-flight (`ensureRefreshed`) y reintenta una vez. El tenant real
// lo impone el JWT en cada subgraph (RLS) — el header es informativo.
//
// Refs: ADR-0002 (gateway = puerta única), ADR-0003 (auth/RLS), ADR-0005 (view-nintaigo).
import { Client, fetchExchange } from 'urql'
import { authExchange } from '@urql/exchange-auth'
import { authSession } from '../stores/auth-store.js'
import { apiOrigin } from './api-origin.js'
import { ensureRefreshed } from './api.js'

const GQL_URL = `${apiOrigin()}/graphql`

function withAuth(init: RequestInit | undefined, token: string | null): RequestInit {
  const sess = authSession()
  // IMPORTANTE: urql pasa `init.headers` como objeto `Headers` (no plain object).
  // Hacer `{ ...init.headers }` sobre un Headers da `{}` y BORRA el
  // `content-type: application/json` que urql puso → Apollo lo bloquea como
  // CSRF ("specify a content-type header…"). Por eso mergeamos vía `new Headers`,
  // que copia bien Headers | array | objeto y preserva el content-type.
  const headers = new Headers(init?.headers ?? {})
  if (sess?.tenantId) headers.set('x-dhk8-tenant-id', sess.tenantId)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return { ...init, credentials: 'include', headers }
}

// Fetch con auth + rotación del access token ante un 401 de TRANSPORTE.
const gqlFetch: typeof fetch = async (input, init) => {
  const sess = authSession()
  let res = await fetch(input, withAuth(init, sess?.token ?? null))
  if (res.status === 401) {
    const fresh = await ensureRefreshed()
    if (fresh) res = await fetch(input, withAuth(init, fresh))
  }
  return res
}

// authExchange: cubre el otro caso — errores de auth a NIVEL GraphQL. El
// gateway/subgraph normalmente responde 200 + `errors` con code UNAUTHENTICATED
// (no un 401 de transporte), así que el refresh del fetch no se dispara.
// `didAuthError` detecta ese error → `refreshAuth` (mismo single-flight
// `ensureRefreshed`) → urql REINTENTA la operación; el retry pasa por `gqlFetch`
// que re-lee el token ya rotado. Headers los sigue poniendo `gqlFetch`
// (addAuthToOperation es no-op). Ref: ADR-0003.
const auth = authExchange(async () => ({
  addAuthToOperation: (operation) => operation,
  didAuthError: (error) =>
    error.graphQLErrors.some(
      (e) =>
        (e.extensions as { code?: unknown } | undefined)?.code === 'UNAUTHENTICATED' ||
        /tenant scope required|present a valid JWT|UNAUTHENTICATED/i.test(e.message),
    ),
  refreshAuth: async () => {
    await ensureRefreshed()
  },
}))

export const gqlClient = new Client({
  url: GQL_URL,
  // FORZAR POST: urql por defecto manda las QUERIES como GET (query en el
  // querystring, sin content-type). El gateway (Apollo) bloquea los GET por
  // prevención de CSRF ("specify a content-type… or apollo-require-preflight").
  // Con POST sale `content-type: application/json` y Apollo lo acepta.
  preferGetMethod: false,
  // SIN cacheExchange (document cache): el admin es back-office de bajo tráfico y
  // el cache-first hacía que reload() tras crear/editar/eliminar sirviera datos
  // viejos (delete devuelve Boolean, sin __typename → urql no invalidaba) → el
  // usuario tenía que refrescar a mano. Sin cache, toda lectura va a la red y la
  // vista refleja el cambio al instante en TODOS los módulos. (catalog.ts ya
  // forzaba network-only por esto mismo; ahora es global.)
  exchanges: [auth, fetchExchange],
  fetch: gqlFetch,
})

// Hook de smoke/diagnóstico (solo dev): permite probar el cliente desde la
// consola/Playwright sin montar UI. `window.__gqlClient.query('{ __typename }').toPromise()`.
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  ;(window as unknown as { __gqlClient?: Client }).__gqlClient = gqlClient
  // F0.5: carga la query tipada de muestra (registra window.__typedSmoke).
  void import('./queries/smoke.queries.js')
}
